import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "../App";
import { ArrowLeft, TrendingUp, Zap, Trophy, Medal, Crown, Flame, Calendar, BookOpen } from "lucide-react";

const TABS = [
  { id: "xp", label: "BTC XP", icon: Zap },
  { id: "whales", label: "Whales", icon: Crown },
  { id: "weekly", label: "Weekly", icon: Calendar },
  { id: "rabbit_hole", label: "Rabbit Hole", icon: BookOpen },
];

const CUSTOM_TITLES = {
  title_sovereign: "Sovereign",
  title_whale: "Whole Coiner",
};

export default function Leaderboard() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("xp");
  const [data, setData] = useState({ leaderboard: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/rewards/leaderboard?tab=${tab}`).then(res => {
      setData(res.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [tab]);

  const leaders = data.leaderboard || [];
  const topColors = ["text-[#F7931A]", "text-zinc-300", "text-orange-600"];

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="leaderboard-page">
      <header className="sticky top-0 z-50 glass-darker border-b border-white/5">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <button onClick={() => navigate("/rewards")} className="flex items-center gap-2 text-zinc-400 hover:text-white" data-testid="leaderboard-back-btn">
            <ArrowLeft size={20} />
          </button>
          <h1 className="font-heading text-xl font-bold text-white">LEADERBOARD</h1>
          <TrendingUp className="text-[#F7931A]" size={20} />
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6 space-y-6">
        {/* Tab Switcher */}
        <div className="flex gap-2 bg-[#0A0A0A] p-1 rounded-xl border border-white/5">
          {TABS.map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium transition-all ${
                tab === t.id ? "bg-[#F7931A] text-black" : "text-zinc-400 hover:text-white"
              }`}
              data-testid={`tab-${t.id}`}
            >
              <t.icon size={16} />
              <span className="hidden sm:inline">{t.label}</span>
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <div className="w-10 h-10 border-4 border-[#F7931A] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <AnimatePresence mode="wait">
            <motion.div key={tab} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>
              {/* Top 3 Podium (BTC XP & Whales) */}
              {tab !== "weekly" && tab !== "rabbit_hole" && leaders.length >= 3 && (
                <div className="flex items-end justify-center gap-4 mb-8 pt-4">
                  {[1, 0, 2].map((idx) => {
                    const leader = leaders[idx];
                    if (!leader) return null;
                    const isFirst = idx === 0;
                    const customTitle = CUSTOM_TITLES[leader.custom_title];
                    return (
                      <motion.div key={idx}
                        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.1 }}
                        className={`text-center ${isFirst ? "pb-4" : ""}`}
                      >
                        <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-2 ${
                          isFirst ? "bg-[#F7931A]/20 btc-glow" : "bg-zinc-800"
                        }`}>
                          {idx === 0 ? <Trophy className="text-[#F7931A]" size={28} /> :
                           idx === 1 ? <Medal className="text-zinc-300" size={24} /> :
                           <Medal className="text-orange-600" size={24} />}
                        </div>
                        <p className={`font-heading font-bold ${topColors[idx]} ${isFirst ? "text-lg" : "text-sm"}`}>
                          {leader.first_name}
                        </p>
                        {customTitle && (
                          <p className="text-purple-400 text-xs font-bold">{customTitle}</p>
                        )}
                        <p className="font-mono text-white text-xs">
                          {tab === "whales"
                            ? `${leader.total_xp_spent?.toLocaleString()} spent`
                            : `${leader.total_lifetime_xp?.toLocaleString()} BTC XP`}
                        </p>
                      </motion.div>
                    );
                  })}
                </div>
              )}

              {/* Weekly Quiz Header */}
              {tab === "weekly" && (
                <div className="bg-[#0A0A0A] border border-[#F7931A]/20 rounded-xl p-4 mb-4 text-center">
                  <Calendar className="text-[#F7931A] mx-auto mb-2" size={24} />
                  <h3 className="text-white font-bold">{data.quiz_topic || "Weekly Quiz"}</h3>
                  <p className="text-zinc-500 text-sm">{leaders.length} participants</p>
                </div>
              )}

              {/* Rabbit Hole Header */}
              {tab === "rabbit_hole" && (
                <div className="bg-[#0A0A0A] border border-purple-500/20 rounded-xl p-4 mb-4 text-center">
                  <BookOpen className="text-purple-400 mx-auto mb-2" size={24} />
                  <h3 className="text-white font-bold">Rabbit Hole Quiz Masters</h3>
                  <p className="text-zinc-500 text-sm">{leaders.length} readers ranked by total correct answers</p>
                </div>
              )}

              {/* Full List */}
              <div className="space-y-2">
                {leaders.map((leader, i) => (
                  <motion.div key={i}
                    initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.03 }}
                    className={`card-dark p-4 flex items-center gap-4 ${i < 3 ? "border-[#F7931A]/20" : ""}`}
                    data-testid={`leaderboard-row-${i}`}
                  >
                    <span className={`font-mono text-lg font-bold w-8 text-center ${i < 3 ? topColors[i] : "text-zinc-600"}`}>
                      {i + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-white text-sm font-medium truncate">{leader.first_name}</p>
                        {CUSTOM_TITLES[leader.custom_title] && (
                          <span className="text-purple-400 text-xs font-bold px-1.5 py-0.5 bg-purple-400/10 rounded border border-purple-400/20">
                            {CUSTOM_TITLES[leader.custom_title]}
                          </span>
                        )}
                      </div>
                      <p className="text-zinc-500 text-xs">{leader.xp_title || `Lv.${leader.xp_level || 1}`}</p>
                    </div>
                    <div className="text-right">
                      {tab === "xp" && (
                        <>
                          <div className="flex items-center gap-1">
                            <Zap className="text-[#F7931A]" size={14} />
                            <span className="font-mono text-white text-sm">{leader.total_lifetime_xp?.toLocaleString()}</span>
                          </div>
                          <p className="text-zinc-500 text-xs">Lv.{leader.xp_level || 1}</p>
                        </>
                      )}
                      {tab === "whales" && (
                        <>
                          <div className="flex items-center gap-1">
                            <Crown className="text-purple-400" size={14} />
                            <span className="font-mono text-purple-400 text-sm">{leader.total_xp_spent?.toLocaleString()}</span>
                          </div>
                          <p className="text-zinc-500 text-xs">{leader.total_purchases} buys</p>
                        </>
                      )}
                      {tab === "weekly" && (
                        <>
                          <span className="font-mono text-[#F7931A] text-sm font-bold">{leader.correct}/{leader.total}</span>
                          <p className="text-zinc-500 text-xs">correct</p>
                        </>
                      )}
                      {tab === "rabbit_hole" && (
                        <>
                          <div className="flex items-center gap-1">
                            <BookOpen className="text-purple-400" size={14} />
                            <span className="font-mono text-purple-400 text-sm font-bold">{leader.total_correct}/{leader.total_questions}</span>
                          </div>
                          <p className="text-zinc-500 text-xs">{leader.quizzes_taken} quiz{leader.quizzes_taken !== 1 ? "zes" : ""} | {leader.accuracy}%</p>
                        </>
                      )}
                    </div>
                  </motion.div>
                ))}
                {leaders.length === 0 && (
                  <div className="text-center py-12">
                    <p className="text-zinc-500">{tab === "weekly" ? "No weekly quiz completions yet. Check back Monday!" : tab === "whales" ? "No BTC XP spenders yet. Visit the BTC XP Store!" : tab === "rabbit_hole" ? "No Rabbit Hole quizzes completed yet. Read an article and take the quiz!" : "No data yet. Be the first!"}</p>
                  </div>
                )}
              </div>
            </motion.div>
          </AnimatePresence>
        )}
      </main>
    </div>
  );
}
